<template>
    <div class="flex flex-col h-screen-index">
        <div>
            <div class="flex flex-col md:flex-row">
                <!-- 移动端搜索容器 -->
                <div class="md:hidden" v-if="getUserRole === 'V2'">
                    <div class="relative bg-base-100 shadow-sm rounded-xl m-2 p-4">
                        <!-- 搜索标题 -->
                        <div class="flex items-center mb-3">
                            <svg class="w-5 h-5 text-primary mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <h3 class="text-lg font-medium text-base-content">搜索音乐</h3>
                        </div>
                        
                        <!-- 搜索输入框 -->
                        <div class="relative mb-4">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg class="w-4 h-4 text-base-content/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input type="text" 
                                class="input input-bordered w-full pl-10 pr-4 py-2 bg-base-100 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                                placeholder="搜索艺术家、专辑或歌曲..." 
                                v-model="searchTerm" 
                                @input="handleInput"
                                autocomplete="off" 
                                @click="showResults = !showResults" />
                        </div>

                        <!-- 搜索结果容器 -->
                        <div v-if="showResults">
                            <div class="bg-base-200/30 border border-base-200 rounded-lg backdrop-blur-sm max-h-80 overflow-y-auto">
                                <!-- 艺术家结果 -->
                                <div v-if="suggestions.artists && suggestions.artists.length > 0" class="p-2">
                                    <div class="px-3 py-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider border-b border-base-200/50">
                                        <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        艺术家
                                    </div>
                                    <div class="py-1">
                                        <div v-for="(artist, index) in suggestions.artists" :key="'at' + index"
                                            class="flex items-center px-3 py-2 cursor-pointer hover:bg-primary/10 rounded-md transition-colors duration-150 group"
                                            @click="redirectArtist(artist)">
                                            <div class="w-8 h-8 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full flex items-center justify-center mr-3 group-hover:from-primary/30 group-hover:to-secondary/30 transition-all duration-150">
                                                <svg class="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                                </svg>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="text-sm font-medium text-base-content truncate">{{ artist.name }}</p>
                                                <p class="text-xs text-base-content/60">{{ artist.albumCount }} 张专辑</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 专辑结果 -->
                                <div v-if="suggestions.albums && suggestions.albums.length > 0" class="p-2">
                                    <div class="px-3 py-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider border-b border-base-200/50">
                                        <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                        专辑
                                    </div>
                                    <div class="py-1">
                                        <div v-for="(album, index) in suggestions.albums" :key="'al' + index"
                                            class="flex items-center px-3 py-2 cursor-pointer hover:bg-primary/10 rounded-md transition-colors duration-150 group"
                                            @click="redirectAlbum(album)">
                                            <div class="w-8 h-8 bg-gradient-to-br from-accent/20 to-primary/20 rounded-md flex items-center justify-center mr-3 group-hover:from-accent/30 group-hover:to-primary/30 transition-all duration-150">
                                                <svg class="w-4 h-4 text-accent" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zM7 12c0-.55.45-1 1-1s1 .45 1 1-.45 1-1 1-1-.45-1-1zm10 0c0 .55-.45 1-1 1s-1-.45-1-1 .45-1 1-1 1 .45 1 1zm-5 0c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z" />
                                                </svg>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="text-sm font-medium text-base-content truncate">{{ album.name }}</p>
                                                <p class="text-xs text-base-content/60">{{ album.artist }} • {{ album.year }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 歌曲结果 -->
                                <div v-if="suggestions.tracks && suggestions.tracks.length > 0" class="p-2">
                                    <div class="px-3 py-2 text-xs font-semibold text-base-content/60 uppercase tracking-wider border-b border-base-200/50">
                                        <svg class="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                        歌曲
                                    </div>
                                    <div class="py-1">
                                        <div v-for="(track, index) in suggestions.tracks" :key="'tr' + index"
                                            class="flex items-center px-3 py-2 cursor-pointer hover:bg-primary/10 rounded-md transition-colors duration-150 group"
                                            @click="redirectTrack(track)">
                                            <div class="w-8 h-8 bg-gradient-to-br from-secondary/20 to-accent/20 rounded-md flex items-center justify-center mr-3 group-hover:from-secondary/30 group-hover:to-accent/30 transition-all duration-150">
                                                <svg class="w-4 h-4 text-secondary" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
                                                </svg>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <p class="text-sm font-medium text-base-content truncate">{{ track.title }}</p>
                                                <p class="text-xs text-base-content/60">{{ track.artist }} • {{ track.album }} • {{ track.year }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <!-- 无结果提示 -->
                                <div v-if="searchTerm && (!suggestions.artists || suggestions.artists.length === 0) && (!suggestions.albums || suggestions.albums.length === 0) && (!suggestions.tracks || suggestions.tracks.length === 0)"
                                    class="p-6 text-center">
                                    <svg class="w-12 h-12 text-base-content/30 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                    <p class="text-base-content/60 text-sm">未找到相关结果</p>
                                    <p class="text-base-content/40 text-xs mt-1">尝试使用不同的关键词</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="hidden md:flex bg-base-100 md:w-1/2 md:h-60 shadow-xl rounded-xl m-2 flex flex-col md:flex-row md:justify-between">
                    <div class="p-8">
                        <div class="text-secondary stat-value truncate">Welcome back {{getUsername}}</div>
                        <div class="mt-4 indent-4 text-gray-500">
                            {{ welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)] }}
                        </div>
                        <div class="mt-4 indent-4 flex" v-if="track">
                            <div class="text-gray-500 text-sm">
                                正在播放:
                            </div>
                            <div class="text-gray-500 hover:underline cursor-pointer text-sm" @click="handleRedirect('playing')">
                                {{track.artist}}-{{track.title}}
                            </div>
                        </div>
                    </div>
                    <img src="/static/dist/img/play_music.svg" alt="Album" class="h-64 z-1" />
                </div>
                <div class="m-2 flex flex-col grow">
                    <div class="stats stats-vertical md:stats-horizontal shadow-xl">
                        <div class="stat">
                            <div class="stat-figure text-primary">
                                <svg t="1695911542390" class="icon" viewBox="0 0 1024 1024" version="1.1"
                                    xmlns="http://www.w3.org/2000/svg" p-id="6940" width="32" height="32" fill="#4818e5">
                                    <path
                                        d="M727.168548 648.936789c0.00614 0 0.010233-0.002047 0.01535-0.002047 0.008186 0 0.01535 0.002047 0.021489 0.002047L727.168548 648.936789zM864.120686 785.871531l0-19.562545c0 64.823378-61.306274 117.374244-136.935766 117.374244-75.626421 0-136.935766-52.549843-136.935766-117.374244 0-47.939849 33.633005-89.020476 81.690534-107.244536l0.062422 0.121773c4.909823-2.220574 10.31902-3.52529 16.058744-3.52529 21.608134 0 39.125089 17.517979 39.125089 39.125089 0 14.296611-7.748475 26.671453-19.194154 33.498952l0.575098 1.101077c-23.228027 5.211698-40.067554 19.683295-40.067554 36.923958 0 21.608134 26.276457 39.125089 58.68661 39.125089 32.413224 0 58.68661-17.515932 58.68661-39.125089 0-0.563842-0.212848-1.085728-0.248663-1.64343l18.588357-115.728768-0.281409 0 42.135656-273.874601-314.502927 0c-21.60711 0-39.125089-17.515932-39.125089-39.123043 0-21.61018 17.517979-39.125089 39.125089-39.125089l326.540078 0 6.019087-39.126112c0-21.608134-17.515932-39.123043-39.125089-39.123043l-352.119663 0c-21.608134 0-39.125089 17.513886-39.125089 39.123043l-78.249155 508.62002c0 64.823378-61.310368 117.374244-136.937812 117.374244S81.630159 831.132364 81.630159 766.308987s61.306274-117.374244 136.933719-117.374244c27.913746 0 53.809533 7.241938 75.459622 19.532869l61.47819-430.339142c0-54.021358 43.790342-97.8117 97.8117-97.8117l391.245775 0c54.019311 0 97.809653 43.790342 97.809653 97.8117L864.120686 785.871531zM218.563878 727.183897c-32.411177 0-58.68661 17.517979-58.68661 39.125089 0 21.608134 26.276457 39.125089 58.68661 39.125089 32.413224 0 58.68661-17.515932 58.68661-39.125089C277.251511 744.701876 250.977102 727.183897 218.563878 727.183897z"
                                        fill="" p-id="6941"></path>
                                </svg>
                            </div>
                            <div class="stat-title">全部歌曲</div>
                            <div class="stat-value text-primary cursor-pointer" @click="handleRedirect('tracks')">{{ musicCount.track_count }}</div>
                            <div class="stat-desc">{{musicCount.track_mom}}% more than last month</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg t="1695911827315" class="icon" viewBox="0 0 1024 1024" version="1.1"
                                    xmlns="http://www.w3.org/2000/svg" p-id="12843" width="32" height="32" fill="#dc2fb3">
                                    <path
                                        d="M548.181333 857.565867a68.266667 68.266667 0 0 1-48.469333-20.0704L180.224 517.870933a68.266667 68.266667 0 0 1 0-96.802133L472.2688 129.024a174.08 174.08 0 0 1 245.76 0l170.666667 170.530133a174.08 174.08 0 0 1 0 245.76L596.650667 837.495467a68.266667 68.266667 0 0 1-48.469334 20.0704z m-9.8304-58.709334a13.653333 13.653333 0 0 0 19.6608 0l292.0448-291.908266a119.466667 119.466667 0 0 0 0-168.7552L679.5264 167.662933a119.466667 119.466667 0 0 0-168.7552 0L218.453333 459.707733a13.653333 13.653333 0 0 0 0 19.6608z"
                                        fill="" p-id="12844"></path>
                                    <path
                                        d="M366.728533 900.437333a78.097067 78.097067 0 0 1-55.432533-22.9376L140.219733 706.423467a78.506667 78.506667 0 0 1 0-110.865067L218.453333 517.461333a28.125867 28.125867 0 0 1 38.638934 0l243.165866 243.3024a27.306667 27.306667 0 0 1 0 38.638934l-78.097066 78.097066a78.097067 78.097067 0 0 1-55.432534 22.9376z m-129.024-324.949333l-58.845866 58.709333a23.7568 23.7568 0 0 0 0 33.5872l171.076266 171.076267a23.7568 23.7568 0 0 0 33.5872 0l58.845867-58.845867zM612.488533 496.4352l-23.3472-23.893333a8.6016 8.6016 0 0 1 0.8192-12.6976l69.495467-54.613334a14.609067 14.609067 0 0 0-9.8304-26.077866l-125.064533 7.099733a14.609067 14.609067 0 0 0-9.557334 24.712533l23.3472 23.893334a8.6016 8.6016 0 0 1-0.8192 12.6976l-69.495466 54.613333A14.609067 14.609067 0 0 0 477.866667 528.384l125.064533-7.099733a14.609067 14.609067 0 0 0 9.557333-24.849067z"
                                        p-id="12845"></path>
                                </svg>
                            </div>
                            <div class="stat-title">歌曲大小</div>
                            <div class="stat-value text-secondary">{{ musicCount.song_size }}</div>
                            <div class="stat-desc">{{musicCount.track_mom}}% more than last month</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <div class="avatar">
                                    <div class="w-16">
                                        <img src="/static/dist/img/music_null-cutout.png" />
                                    </div>
                                </div>
                            </div>
                            <div class="stat-value cursor-pointer" @click="handleRedirect('progress')">{{ musicCount.finished_rate }}</div>
                            <div class="stat-title">刮削完整度</div>
                            <div class="stat-desc text-secondary">{{musicCount.active_tasks_count}} tasks remaining</div>
                        </div>
                    </div>
                    <div class="stats stats-vertical md:stats-horizontal shadow-xl mt-4">

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg class="inline-block w-8 h-8 stroke-current" viewBox="0 0 1024 1024" version="1.1"
                                    xmlns="http://www.w3.org/2000/svg" p-id="4475" width="32" height="32" fill="#4f14ee">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M511.701333 511.701333m-85.632 0a85.632 85.632 0 1 0 171.264 0 85.632 85.632 0 1 0-171.264 0Z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M512 85.333333C276.736 85.333333 85.333333 276.736 85.333333 512s191.402667 426.666667 426.666667 426.666667 426.666667-191.402667 426.666667-426.666667S747.264 85.333333 512 85.333333z m0 768c-188.202667 0-341.333333-153.130667-341.333333-341.333333s153.130667-341.333333 341.333333-341.333333 341.333333 153.130667 341.333333 341.333333-153.130667 341.333333-341.333333 341.333333z"
                                        fill="" p-id="4477"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M512 256c-141.184 0-256 114.816-256 256h85.333333c0-94.122667 76.544-170.666667 170.666667-170.666667V256z"
                                        fill="" p-id="4478"></path>
                                </svg>
                            </div>
                            <div class="stat-title">全部专辑</div>
                            <div class="stat-value text-secondary cursor-pointer" @click="handleRedirect('albums')">{{ musicCount.album_count }}</div>
                            <div class="stat-desc">↗ {{musicCount.album_diff}} ({{musicCount.album_mom}})%</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg t="1695911766499" class="icon" viewBox="0 0 1024 1024" version="1.1"
                                    xmlns="http://www.w3.org/2000/svg" p-id="11691" width="32" height="32" fill="#dc2fb3">
                                    <path
                                        d="M535.253333 255.274667a170.666667 170.666667 0 0 0 234.154667 233.045333c-51.2-23.850667-104.618667-61.866667-138.24-95.488-34.048-34.048-72.149333-87.04-95.914667-137.557333z m65.706667-63.829334c12.928 40.277333 57.301333 107.818667 90.538667 141.056 32.682667 32.682667 99.072 76.074667 141.44 89.813334a170.666667 170.666667 0 0 0-231.936-230.869334z m-9.173333 389.290667a256.768 256.768 0 0 1-148.522667-148.522667l-259.84 373.504 34.858667 34.858667 373.504-259.84z m129.834666 13.653333L208.384 951.424l-135.808-135.808L429.653333 302.378667A256.042667 256.042667 0 0 1 938.666667 341.333333a256.042667 256.042667 0 0 1-217.045334 253.056z"
                                        p-id="11692"></path>
                                </svg>
                            </div>
                            <div class="stat-title">全部艺术家</div>
                            <div class="stat-value text-primary cursor-pointer" @click="handleRedirect('artists')">{{ musicCount.artist_count }}</div>
                            <div class="stat-desc">↗︎ {{ musicCount.artist_diff }} ({{ musicCount.artist_mom }}%)</div>
                        </div>

                        <div class="stat">
                            <div class="stat-figure text-secondary">
                                <svg t="1695911923641" class="icon" viewBox="0 0 1024 1024" version="1.1"
                                    xmlns="http://www.w3.org/2000/svg" p-id="13865" width="32" height="32" fill="#4818e5">
                                    <path
                                        d="M512 229.034667c82.310095 0 157.793524 29.135238 216.746667 77.628952l-52.028953 52.053333A267.044571 267.044571 0 0 0 512 302.153143c-148.114286 0-268.190476 120.100571-268.190476 268.190476 0 148.138667 120.07619 268.190476 268.190476 268.190476s268.190476-120.05181 268.190476-268.190476c0-14.750476-1.194667-29.257143-3.486476-43.398095l60.513524-60.513524c10.48381 32.768 16.11581 67.705905 16.115809 103.936 0 188.513524-152.81981 341.333333-341.333333 341.333333s-341.333333-152.81981-341.333333-341.333333 152.81981-341.333333 341.333333-341.333333zM808.374857 307.2l51.492572 51.95581-312.076191 309.345523-143.969524-143.969523 51.736381-51.712 92.452572 92.452571 260.388571-258.072381zM636.123429 109.714286v77.58019H377.539048V109.714286H636.099048z"
                                        p-id="13866"></path>
                                </svg>
                            </div>
                            <div class="stat-title">歌曲时长</div>
                            <div class="stat-value" id="example-black">{{ musicCount.song_duration }}</div>
                            <div class="stat-desc">Total elapsed time {{ musicCount.song_duration_raw }}s</div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <div class="flex-1">
            <div class="flex flex-col md:flex-row h-full">
                <div class="bg-base-100 shadow-xl rounded-xl m-2 flex-1 overflow-y-auto p-8">
                    <div id="demo" class="h-96 md:h-full"></div>
                </div>
                <div class="bg-base-100 shadow-xl rounded-xl m-2 md:w-96 overflow-y-auto">
                    <div class="mt-4 mb-1 ml-4 flex justify-between items-end">
                        <div class="font-medium">排行榜</div>
                        <div class="mr-4 font-light text-sm underline cursor-pointer" @click="handleRedirect('record')">更多</div>
                    </div>
                    <div role="tablist" class="tabs tabs-boxed bg-base-100 ml-2 !flex">
                        <a role="tab" :class="{ 'tab': true, 'tab-active': tabSelect === 'track' }"
                            @click="tabSelect = 'track'">歌曲</a>
                        <a role="tab" :class="{ 'tab': true, 'tab-active': tabSelect === 'album' }"
                            @click="tabSelect = 'album'">专辑</a>
                        <a role="tab" :class="{ 'tab': true, 'tab-active': tabSelect === 'artist' }"
                            @click="tabSelect = 'artist'">艺术家</a>
                    </div>
                    <div>
                        <div v-if="tabSelect === 'track'">
                            <div class="overflow-x-auto">
                                <table class="table">
                                    <!-- head -->
                                    <tbody>
                                        <!-- row 1 -->
                                        <tr v-for="(item,index) in auditLogData.track_list" :key="index" class="font-light">
                                            <th>{{ index + 1 }}</th>
                                            <td>{{ item.record }}</td>
                                            <td>{{ item.count }}次</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else-if="tabSelect === 'album'">
                            <div class="overflow-x-auto">
                                <table class="table">
                                    <!-- head -->
                                    <tbody>
                                        <!-- row 1 -->
                                        <tr v-for="(item,index) in auditLogData.album_list" :key="index" class="font-light">
                                            <th>{{ index + 1 }}</th>
                                            <td>{{ item.record }}</td>
                                            <td>{{ item.count }}次</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div v-else-if="tabSelect === 'artist'">
                            <div class="overflow-x-auto">
                                <table class="table">
                                    <!-- head -->
                                    <tbody>
                                        <!-- row 1 -->
                                        <tr v-for="(item,index) in auditLogData.artist_list" :key="index" class="font-light">
                                            <th>{{ index + 1 }}</th>
                                            <td>{{ item.record }}</td>
                                            <td>{{ item.count }}次</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
    import {mapGetters} from 'vuex'
    import culori from "culori";
    export default {
        data() {
            return {
                size: 'small',
                musicCount: {},
                auditLogData: [],
                tabSelect: 'track',
                // 搜索相关数据
                searchTerm: '',
                showResults: false,
                suggestions: {},
                searchTimeout: null,
                role: '',
                welcomeMessages: [
                    "我将刮削标签的过程视为与我的音乐藏品共度的美好时光，而不仅仅是一项单调乏味的任务。",
                    "每一次指尖抚过标签的刮削，都像是与音乐藏品展开一场温柔对话, 这不再是机械重复的劳作，而是我与旋律记忆的重逢时刻。",
                    "那些被岁月封存的音符、情感与故事，在轻缓的动作中悄然苏醒, 让时光也变得缱绻悠长。",
                    "刮刀游走在标签边缘的沙沙声，恰似音乐藏品轻声呢喃，将尘封的韵律与故事娓娓道来，让枯燥的工作化作一场跨越时空的音乐漫游。",
                    "刮削标签时，指尖触碰的不只是纸张与胶水，更是每一段旋律的脉搏。那些被收纳的乐章，在细微的动作里重新鲜活，编织成独属于我的音乐时光诗篇。",
                    "在音乐的海洋中，我与标签的邂逅，是一场关于旋律、情感与时光的浪漫之旅。每一次刮削，都是对音乐记忆的深情致敬，让时间也变得温柔起来。",
                    "多年后摩挲着泛黄的收藏册，那时以为寻常的整理时光，竟成了串起毕生音乐热爱的金线，每一次标签剥落都在未来的记忆里绽放成永不褪色的音符。"
                ]
            }
        },
        computed: {
            ...mapGetters(['getUsername', 'track', 'getUserRole'])
        },
        created() {
            this.fetchMusicCount()
            this.fetchAuditLog()
        },
        mounted() {
        },
        methods: {
            oklchToHex(oklchString) {
                const culori = require('culori');

                const match = oklchString.match(/oklch\(([\d.]+)\s+([\d.]+)\s+([\d.]+)\)/);
                if (!match) {
                    throw new Error('Invalid OKLCH string');
                }

                const [, l, c, h] = match.map(Number);

                // 使用culori将OKLCH转换为RGB
                const rgb = culori.converter('rgb')({mode: 'oklch', l, c, h});

                // 将RGB值限制在0-1范围内，然后转换为0-255范围的整数
                const clamp = (value) => Math.max(0, Math.min(1, value));
                const r = Math.round(clamp(rgb.r) * 255);
                const g = Math.round(clamp(rgb.g) * 255);
                const b = Math.round(clamp(rgb.b) * 255);

                // 将RGB转换为16进制
                const toHex = (value) => {
                    const hex = value.toString(16);
                    return hex.length === 1 ? '0' + hex : hex;
                };

                return '#' + toHex(r) + toHex(g) + toHex(b);
            },
            handlePageChange(page) {
                this.pagination.current = page
            },
            remove(row) {
                const index = this.data.indexOf(row)
                if (index !== -1) {
                    this.data.splice(index, 1)
                }
            },
            draw() {
                const myChart = this.$echarts.init(document.getElementById('demo'))
                let accentColor = window.getComputedStyle(document.getElementById('example-black'))['color']
                accentColor = this.oklchToHex(accentColor)
                const option = {
                    color: ['#80FFA5', '#00DDFF', '#37A2FF', '#FF0087', '#FFBF00'],
                    title: {
                        text: '音乐月度趋势',
                        textStyle: {
                            color: accentColor
                        }
                    },
                    tooltip: {
                        trigger: 'axis',
                        axisPointer: {
                            type: 'cross',
                            label: {
                                backgroundColor: '#6a7985'
                            }
                        }
                    },
                    legend: {
                        data: ['歌曲', '专辑', '艺术家'],
                        textStyle: {
                            color: accentColor
                        }
                    },
                    toolbox: {
                        feature: {
                            saveAsImage: {}
                        }
                    },
                    grid: {
                        left: '3%',
                        right: '4%',
                        bottom: '3%',
                        containLabel: true
                    },
                    xAxis: [
                        {
                            type: 'category',
                            boundaryGap: false,
                            data: this.musicCount.trends.x_axis
                        }
                    ],
                    yAxis: [
                        {
                            type: 'value'
                        }
                    ],
                    series: [
                        {
                            name: '歌曲',
                            type: 'line',
                            stack: 'Total',
                            smooth: true,
                            lineStyle: {
                                width: 0
                            },
                            showSymbol: false,
                            areaStyle: {
                                opacity: 0.8,
                                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    {
                                        offset: 0,
                                        color: 'rgb(128, 255, 165)'
                                    },
                                    {
                                        offset: 1,
                                        color: 'rgb(1, 191, 236)'
                                    }
                                ])
                            },
                            emphasis: {
                                focus: 'series'
                            },
                            data: this.musicCount.trends.track
                        },
                        {
                            name: '专辑',
                            type: 'line',
                            stack: 'Total',
                            smooth: true,
                            lineStyle: {
                                width: 0
                            },
                            showSymbol: false,
                            areaStyle: {
                                opacity: 0.8,
                                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    {
                                        offset: 0,
                                        color: 'rgb(0, 221, 255)'
                                    },
                                    {
                                        offset: 1,
                                        color: 'rgb(77, 119, 255)'
                                    }
                                ])
                            },
                            emphasis: {
                                focus: 'series'
                            },
                            data: this.musicCount.trends.album
                        },
                        {
                            name: '艺术家',
                            type: 'line',
                            stack: 'Total',
                            smooth: true,
                            lineStyle: {
                                width: 0
                            },
                            showSymbol: false,
                            areaStyle: {
                                opacity: 0.8,
                                color: new this.$echarts.graphic.LinearGradient(0, 0, 0, 1, [
                                    {
                                        offset: 0,
                                        color: 'rgb(55, 162, 255)'
                                    },
                                    {
                                        offset: 1,
                                        color: 'rgb(116, 21, 219)'
                                    }
                                ])
                            },
                            emphasis: {
                                focus: 'series'
                            },
                            data: this.musicCount.trends.artist
                        }
                    ]
                }
                myChart.setOption(option)
            },
            fetchMusicCount() {
                this.$api.Task.getMusicCount().then(res => {
                    if (res.result) {
                        this.musicCount = res.data
                        this.draw()
                        if (this.musicCount.track_count === 0) {
                            this.$myMsg.notify({
                            title: '暂无歌曲',
                            content: '请从操作台-导入收藏中添加音乐吧！',
                            type: 'warning',
                            time: 2000,
                            url: '/'
                        });
                        }
                    }
                })
            },
            fetchAuditLog() {
                this.$api.Task.getListeningRecord({page_size: 8}).then(res => {
                    if (res.result) {
                        this.auditLogData = res.data
                    }
                })
            },
            handleInput() {
                if (this.searchTerm.length > 0) {
                    // 发送AJAX请求获取联想结果
                    if (this.searchTimeout) {
                        clearTimeout(this.searchTimeout)
                    }
                    this.searchTimeout = setTimeout(() => {
                        this.searchTimeout = null
                        this.$api.subsonic.search(this.searchTerm).then((res) => {
                            this.suggestions = res
                            this.showResults = true
                        }).catch(() => {
                            this.suggestions = {}
                            this.showResults = false
                        })
                    }, 300)
                } else {
                    this.suggestions = {}
                    this.showResults = false
                }
            },
            redirectArtist(item) {
                this.$router.push({ path: '/artist/id/' + item.id })
                this.showResults = false
                this.searchTerm = ''
            },
            redirectAlbum(item) {
                this.$router.push({ path: '/album/id/' + item.id })
                this.showResults = false
                this.searchTerm = ''
            },
            redirectTrack(item) {
                this.$router.push({ path: '/album/id/' + item.albumId })
                this.showResults = false
                this.searchTerm = ''
            },
            handleRedirect(v) {
                if (v === 'tracks') {
                    this.$router.push({path: '/tracks/recently-added/'})
                } else if (v === 'albums') {
                    this.$router.push({path: '/albums/recently-added/'})
                } else if (v === 'artists') {
                    this.$router.push({path: '/artists/most-albums/'})
                } else if (v === 'playing') {
                    this.$router.push({path: '/playing'})
                } else if (v === 'progress') {
                    this.$router.push({path: '/progress'})
                } else if (v === 'record') {
                    this.$router.push({path: '/record'})
                }
            }
        }
    }
</script>
<style lang="postcss">
</style>
